// Requirement-drawer-local constants. Keep magic strings/objects here
// so the drawer body and section files stay declarative.
import type { RequirementTabKey } from "../../constants";

/* Drawer sheet dimensions (mirrors Concept/Governance for visual parity). */
export const REQUIREMENT_DRAWER_SHEET_CLS =
  "z-[80] flex h-full w-[1400px] max-w-[95vw] flex-col gap-0 border-l border-border bg-background p-0 sm:max-w-[95vw]";
export const REQUIREMENT_DRAWER_OVERLAY_CLS = "z-[80]";

/* Copy strings surfaced by the drawer shell. */
export const REQUIREMENT_DRAWER_COPY = {
  title: "Requirement Intelligence",
  description:
    "Every normative obligation extracted from this document, decomposed into actor, action, object and constraints, with obligation strength and coverage gaps.",
  loading: "Loading requirement intelligence...",
  headerTitle: "Requirement Intelligence",
  headerBlurb:
    "Every normative obligation extracted from this document, decomposed into actor, action, object and constraints. With obligation strength and coverage gaps.",
  decomposedLabel: "Decomposed",
  footerPrefix: "Requirement validation",
  viewEvidence: "View source evidence",
  layerLabel: "Layer · Requirement",
  frameworkFallback: "SCF",
  extractedLabel: "Extracted",
} as const;

/* Requirements list section copy. */
export const REQUIREMENT_LIST_COPY = {
  header: "Requirements",
  emptyText: "No requirements match this tab.",
  showingPrefix: "Showing",
} as const;

/* Obligation strength copy. */
export const OBLIGATION_STRENGTH_COPY = {
  header: "Obligation strength",
  right: "Modal verb distribution",
  emptyText: "No normative requirements were extracted from this document.",
  totalLabel: "Total",
} as const;

/* Extraction summary copy. */
export const EXTRACTION_SUMMARY_COPY = {
  header: "Extraction summary",
  right: "Normative obligations",
} as const;

/* Decomp cell copy. */
export const DECOMP_LABELS = {
  actor: "Actor",
  action: "Action",
  object: "Object",
  constraints: "Constraints",
  section: "Section",
  page: "Page",
} as const;

/* Tab definitions used by RequirementsListSection (labels built at render). */
export const REQUIREMENT_TAB_KEYS: readonly RequirementTabKey[] = [
  "all",
  "mandatory",
  "prohibited",
  "flagged",
] as const;

export const REQUIREMENT_TAB_LABEL_PREFIX: Record<RequirementTabKey, string> = {
  all: "All",
  mandatory: "Mandatory",
  prohibited: "Prohibited",
  flagged: "Flagged",
};

/* Status-banner tone classes. */
export const STATUS_BANNER_WARN_CLS =
  "border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300";
export const STATUS_BANNER_OK_CLS =
  "border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300";

/* Reused pill/box class strings. */
export const REQ_ID_CHIP_CLS =
  "inline-flex max-w-[220px] items-center overflow-hidden rounded-md bg-violet-100 px-2 py-0.5 text-[11px] font-bold tracking-wide text-violet-700 border border-violet-200 dark:bg-violet-500/15 dark:text-violet-300 dark:border-violet-500/25 whitespace-nowrap text-ellipsis";
export const HEADER_ICON_BOX_CLS =
  "flex size-10 shrink-0 items-center justify-center rounded-lg bg-indigo-100 border border-indigo-200 dark:bg-indigo-500/15 dark:border-indigo-500/25";
export const HEADER_ICON_CLS = "size-5 text-indigo-600 dark:text-indigo-400";
export const META_CHIP_CLS =
  "inline-flex items-center rounded-md border border-border bg-background/60 px-2 py-0.5 text-[11px] text-muted-foreground break-words";
export const META_CHIP_FOREGROUND_CLS =
  "inline-flex items-center rounded-md border border-border bg-background/60 px-2 py-0.5 text-[11px] text-foreground break-words";

/* Pagination button class. */
export const PAGINATION_BTN_CLS =
  "inline-flex size-7 items-center justify-center rounded-md border border-border bg-background text-muted-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed h-auto p-0";

/* Requirement row toggle wrapper — hover + full-width click target. */
export const ROW_TOGGLE_CLS =
  "w-full flex items-start gap-3 p-3 text-left hover:bg-muted/40 transition-colors h-auto justify-start whitespace-normal";

/* Requirements list tab trigger class. */
export const REQ_TAB_TRIGGER_CLS =
  "h-auto flex-none rounded-md border border-border bg-background/60 px-2.5 py-1 text-[11.5px] font-semibold text-muted-foreground data-[state=active]:border-indigo-200 data-[state=active]:bg-indigo-100 data-[state=active]:text-indigo-700 dark:data-[state=active]:border-indigo-500/30 dark:data-[state=active]:bg-indigo-500/15 dark:data-[state=active]:text-indigo-300";
